import {
  type BrandVariants,
  type Theme,
  createDarkTheme,
  createLightTheme,
} from "@fluentui/react-components";
import { create } from "zustand";

type ThemeMode = "light" | "dark";

type ConfigState = {
  themeConfig: {
    mode: ThemeMode;
    theme: Theme;
    primaryColor: string;
  };
  themeOptions: { label: string; value: ThemeMode }[];
  roleConfig: {
    selectedRole: string | null;
    availableRoles: string[];
  };
  initConfig: {
    isInitialized: boolean;
    isInitializing: boolean;
    initializationError: string | null;
  };
};

type Actions = {
  setThemeMode: (mode: ThemeMode) => void;
  setPrimaryColor: (color: string) => void;
};

const defaultBrandColors: BrandVariants = {
  10: "#000000",
  20: "#1a1a1a",
  30: "#333333",
  40: "#4d4d4d",
  50: "#666666",
  60: "#808080",
  70: "#999999",
  80: "#b3b3b3",
  90: "#cccccc",
  100: "#e0e0e0",
  110: "#e8e8e8",
  120: "#f0f0f0",
  130: "#f5f5f5",
  140: "#f9f9f9",
  150: "#fcfcfc",
  160: "#ffffff",
};

/* const defaultBrandColors: BrandVariants = {
  10: "#331F04",
  20: "#4D2F06",
  30: "#663F08",
  40: "#804F0A",
  50: "#995E0C",
  60: "#B36E0E",
  70: "#CC7E10",
  80: "#E68E13",
  90: "#FF9E16",
  100: "#FF9E1B",
  110: "#FFAB3D",
  120: "#FFB85F",
  130: "#FFC581",
  140: "#FFD2A3",
  150: "#FFDFC5",
  160: "#FFECE6",
}; */

const createTheme = (mode: ThemeMode, brandColors: BrandVariants): Theme => {
  return mode === "light"
    ? createLightTheme(brandColors)
    : createDarkTheme(brandColors);
};

const useConfigStore = create<ConfigState & Actions>()((set) => ({
  themeConfig: {
    mode: "light",
    theme: createLightTheme(defaultBrandColors),
    primaryColor: "#03dac6",
  },
  themeOptions: [
    { label: "Light", value: "light" },
    { label: "Dark", value: "dark" },
  ],
  roleConfig: {
    selectedRole: null,
    availableRoles: [],
  },
  dashboardViewType: "myProjects",
  userConfig: {
    userInfo: null,
  },
  initConfig: {
    isInitialized: false,
    isInitializing: false,
    initializationError: null,
  },
  setThemeMode: (mode: ThemeMode) => {
    set((state) => ({
      themeConfig: {
        ...state.themeConfig,
        mode,
        theme: createTheme(mode, defaultBrandColors),
      },
    }));
  },
  setPrimaryColor: (primaryColor: string) => {
    set((state) => ({
      themeConfig: {
        ...state.themeConfig,
        primaryColor,
      },
    }));
  },
  setSelectedRole: (role: string) => {
    set((state) => ({
      roleConfig: {
        ...state.roleConfig,
        selectedRole: role,
      },
    }));
  },
  setAvailableRoles: (roles: string[]) => {
    set((state) => ({
      roleConfig: {
        ...state.roleConfig,
        availableRoles: roles,
        // Set the first role as selected if none is selected yet
        selectedRole:
          state.roleConfig.selectedRole || (roles.length > 0 ? roles[0] : null),
      },
    }));
  },
  setInitializing: (isInitializing: boolean) => {
    set((state) => ({
      initConfig: {
        ...state.initConfig,
        isInitializing,
      },
    }));
  },
  setInitialized: (isInitialized: boolean) => {
    set((state) => ({
      initConfig: {
        ...state.initConfig,
        isInitialized,
        isInitializing: false,
      },
    }));
  },
  setInitializationError: (error: string | null) => {
    set((state) => ({
      initConfig: {
        ...state.initConfig,
        initializationError: error,
        isInitializing: false,
      },
    }));
  },
}));

export default useConfigStore;
